import { fetchBooking } from '@/features/purchase/purchaseService';
import {
  confirmRefundOffers,
  deleteRefundOffers,
  initiateRefundByToken,
} from '@/features/refund/fullRefundService';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { type ApiErrorResponse } from '@/types/api';
import { type BookingDTO, type RefundOffersDTO } from '@/types/dto/agent';
import { TIME_ZONE_OFFSET_PATTERN } from '@/utils/dateTime';
import { showErrorToast } from '@/utils/errorToast';
import { parseRefundUrlParams } from '@/utils/refundUrlParams';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// The backend omits the zone offset on some date-times and `Date.parse` would
// read those as browser-local, so pin them to UTC when they don't carry one.
const parseServerDateTime = (value: string): number =>
  Date.parse(TIME_ZONE_OFFSET_PATTERN.test(value) ? value : `${value}Z`);

// The epoch ms instant the offer(s) stop being confirmable, or `undefined` when
// the backend didn't date them. The earliest deadline governs, since one lapsed
// offer fails the whole confirm.
//
// `validUntil - createdOn` is the server-side lifetime, immune to browser clock
// skew; anchoring it to `receivedAt` charges the response trip against the TTL.
// Without `createdOn` (optional in the schema) we fall back to `validUntil` on
// the local clock — skew-prone, but better than no expiry.
const getOfferDeadline = (
  refundOffers: RefundOffersDTO,
  receivedAt: number
): number | undefined => {
  const deadlines = (refundOffers.refundOffers ?? []).flatMap((offer) => {
    if (!offer.validUntil) {
      return [];
    }

    const validUntil = parseServerDateTime(offer.validUntil);

    if (Number.isNaN(validUntil)) {
      return [];
    }

    const createdOn = offer.createdOn
      ? parseServerDateTime(offer.createdOn)
      : NaN;

    if (Number.isNaN(createdOn)) {
      return [validUntil];
    }

    return [receivedAt + (validUntil - createdOn)];
  });

  return deadlines.length ? Math.min(...deadlines) : undefined;
};

// Drives the refund deep-link flow from the URL params (`booking`,
// `fulfillment`, `refundToken`). Each admission (fulfillment) has its own
// unique link, so a link refers to a single fulfillment: on mount the backend
// validates the token and returns its refund offer, which the view renders
// until the user confirms or cancels it.
//
// The flow is self-contained: it does not touch the manage-booking slice.
export const useRefundDeepLink = () => {
  const paramsRef = useRef(parseRefundUrlParams());
  const navigate = useNavigate();

  // `receivedAt` travels with the offers it dates, so the TTL is always counted
  // from the response that produced these offers.
  const [offerState, setOfferState] = useState<{
    offers: RefundOffersDTO;
    receivedAt: number;
  }>();
  const [booking, setBooking] = useState<BookingDTO>();
  const [pending, setPending] = useState<'confirm' | 'cancel'>();
  const refundOffers = offerState?.offers;

  const failAndLeave = useCallback(
    (error?: ApiErrorResponse) => {
      if (error) {
        showErrorToast(<TransAlert i18nKey="refundFailed" />, error);
      } else {
        toast.error(<TransAlert i18nKey="refundFailed" />);
      }

      navigate('/purchase', { replace: true });
    },
    [navigate]
  );

  const initiate = useCallback(async () => {
    const { bookingId, fulfillment, token } = paramsRef.current;

    if (!bookingId || !fulfillment || !token) {
      failAndLeave();
      return;
    }

    try {
      const offers = await initiateRefundByToken({
        bookingId,
        fulfillmentId: fulfillment,
        refundToken: token,
      });
      const receivedAt = Date.now();

      // The booking names the trip, ticket and passenger(s) being refunded —
      // the offer only carries amounts. A failure here must not block the
      // refund: the page still renders the summary and lets the user confirm.
      setBooking(await fetchBooking(bookingId).catch(() => undefined));

      setOfferState({ offers, receivedAt });
    } catch (e) {
      failAndLeave(e as ApiErrorResponse);
    }
  }, [failAndLeave]);

  // Guard against double-invoke (React StrictMode) firing a redundant second
  // initiate for the same link.
  const initiated = useRef(false);

  useEffect(() => {
    if (initiated.current) return;
    initiated.current = true;

    initiate();
  }, [initiate]);

  // A lapsed offer fails the confirm with an error too ambiguous to map back to
  // expiry, so we retire the page ourselves rather than leave the user with a
  // Confirm button that can no longer work.
  useEffect(() => {
    if (!offerState || pending) {
      return;
    }

    const deadline = getOfferDeadline(offerState.offers, offerState.receivedAt);

    if (deadline === undefined) {
      return;
    }

    const timeout = setTimeout(
      () => failAndLeave(),
      Math.max(0, deadline - Date.now())
    );

    return () => clearTimeout(timeout);
  }, [failAndLeave, offerState, pending]);

  const confirm = useCallback(async () => {
    const { bookingId } = paramsRef.current;

    if (!bookingId || !refundOffers?.refundOffers?.length) {
      return;
    }

    setPending('confirm');

    try {
      await confirmRefundOffers({
        bookingId,
        refundOffers: refundOffers.refundOffers,
      });
    } catch (e) {
      showErrorToast(
        <TransAlert i18nKey="refundFailed" />,
        e as ApiErrorResponse
      );
      setPending(undefined);
      return;
    }

    toast.success(<TransText i18nKey="refundInitiated" />);
    navigate('/purchase', { replace: true });
  }, [navigate, refundOffers]);

  // Releases the offer server-side. Other ways of leaving the page (tab close,
  // reload, back) can't reliably finish a request, so those fall back to the
  // offer lapsing on its own. Once released the link is spent, so there's
  // nothing left to show — hand the user back to the shop with a toast.
  const cancel = useCallback(async () => {
    const { bookingId } = paramsRef.current;

    if (!bookingId || !refundOffers?.refundOffers?.length) {
      return;
    }

    setPending('cancel');

    try {
      await deleteRefundOffers({
        bookingId,
        refundOffers: refundOffers.refundOffers,
      });
    } catch (e) {
      showErrorToast(
        <TransAlert i18nKey="refundFailed" />,
        e as ApiErrorResponse
      );
      setPending(undefined);
      return;
    }

    toast.info(<TransAlert i18nKey="refundCancelled" />);
    navigate('/purchase', { replace: true });
  }, [navigate, refundOffers]);

  return { refundOffers, booking, pending, confirm, cancel };
};
